import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { FolderOpen, Plus, BookOpen, Settings, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiUrl } from '../config/api';

export default function ReadingsDesigner() {
  const { user } = useAuth();
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [classes, setClasses] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);

  // Fetch teacher's classes on mount
  useEffect(() => {
    async function fetchClasses() {
      if (!user || user.type !== 'teacher') {
        setLoading(false);
        return;
      }

      try {
        console.log('[ReadingsDesigner] Fetching classes for teacher:', user.id);
        const response = await fetch(apiUrl(`/api/teachers/${user.id}/classes`));

        if (response.ok) {
          const data = await response.json();
          console.log('[ReadingsDesigner] Loaded classes:', data);
          setClasses(data.classes || []);
        } else {
          console.error('[ReadingsDesigner] Failed to load classes:', response.status);
          setClasses([]);
        }
      } catch (error) {
        console.error('[ReadingsDesigner] Error loading classes:', error);
        setClasses([]);
      } finally {
        setLoading(false);
      }
    }

    fetchClasses();
  }, [user]);

  // Fetch assignments when class is selected
  useEffect(() => {
    async function fetchAssignments() {
      if (!selectedClass || !user) return;

      setAssignmentsLoading(true);
      try {
        console.log('[ReadingsDesigner] Fetching assignments for class:', selectedClass);
        const response = await fetch(
          apiUrl(`/api/teachers/${user.id}/classes/${selectedClass}/assignments`)
        );

        if (response.ok) {
          const data = await response.json();
          console.log('[ReadingsDesigner] Loaded assignments:', data);
          setAssignments(prev => ({
            ...prev,
            [selectedClass]: data.assignments || []
          }));
        } else {
          console.error('[ReadingsDesigner] Failed to load assignments:', response.status);
          setAssignments(prev => ({ ...prev, [selectedClass]: [] }));
        }
      } catch (error) {
        console.error('[ReadingsDesigner] Error loading assignments:', error);
        setAssignments(prev => ({ ...prev, [selectedClass]: [] }));
      } finally {
        setAssignmentsLoading(false);
      }
    }

    fetchAssignments();
  }, [selectedClass, user]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight mb-2 font-serif">Readings Designer</h1>
        <p className="text-lg text-muted-foreground">
          Create and assign readings to your classes
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Classes Column */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">My Classes</CardTitle>
              <Button size="sm" variant="ghost">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <CardDescription>Select a class to manage</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : classes.length > 0 ? (
              classes.map((cls) => (
                <button
                  key={cls.id}
                  onClick={() => {
                    setSelectedClass(cls.id);
                    setSelectedAssignment(null);
                  }}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedClass === cls.id
                      ? 'bg-primary/10 border-primary'
                      : 'hover:bg-accent'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 text-primary" />
                    <div className="flex-1">
                      <div className="font-medium">{cls.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {cls.studentCount} students
                      </div>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No classes yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assignments Column */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Assignments</CardTitle>
              {selectedClass && (
                <Button size="sm" variant="ghost">
                  <Plus className="h-4 w-4" />
                </Button>
              )}
            </div>
            <CardDescription>
              {selectedClass
                ? `Assignments for ${classes.find((c) => c.id === selectedClass)?.name}`
                : 'Select a class to view assignments'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {assignmentsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : selectedClass && assignments[selectedClass]?.length > 0 ? (
              assignments[selectedClass].map((assignment) => (
                <button
                  key={assignment.id}
                  onClick={() => setSelectedAssignment(assignment.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedAssignment === assignment.id
                      ? 'bg-primary/10 border-primary'
                      : 'hover:bg-accent'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-primary" />
                    <div className="flex-1">
                      <div className="font-medium">{assignment.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {assignment.book.title} by {assignment.book.author}
                      </div>
                      {assignment.dueDate && (
                        <div className="text-xs text-muted-foreground">
                          Due: {new Date(assignment.dueDate).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))
            ) : selectedClass ? (
              <div className="text-center py-8 text-muted-foreground">
                <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No assignments yet</p>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No class selected</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Details Column */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Details</CardTitle>
              {selectedAssignment && (
                <Button size="sm" variant="ghost">
                  <Settings className="h-4 w-4" />
                </Button>
              )}
            </div>
            <CardDescription>
              {selectedAssignment
                ? 'Assignment details and settings'
                : 'Select an assignment to view details'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {selectedAssignment && selectedClass && assignments[selectedClass] ? (
              (() => {
                const assignment = assignments[selectedClass].find(a => a.id === selectedAssignment);
                if (!assignment) return null;

                return (
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-medium mb-2">Book Details</h3>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Title:</span>
                          <p className="font-medium">{assignment.book.title}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Author:</span>
                          <p>{assignment.book.author}</p>
                        </div>
                        {assignment.dueDate && (
                          <div>
                            <span className="text-muted-foreground">Due Date:</span>
                            <p>{new Date(assignment.dueDate).toLocaleDateString()}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="font-medium mb-2">Student Progress</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total Students:</span>
                          <span>{assignment.progress.totalStudents}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Started:</span>
                          <span>{assignment.progress.studentsStarted}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Completed:</span>
                          <span>{assignment.progress.studentsCompleted} / {assignment.progress.totalStudents}</span>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <Button variant="destructive" className="w-full">
                      Delete Assignment
                    </Button>
                  </div>
                );
              })()
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Settings className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No assignment selected</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
